// src/config/roles.js

// Re-export checkout constants from the helpers file so other components
// can import role/checkout config from a single location. Single source of
// truth for the actual values lives in src/utils/checkoutHelpers.js.
export {
  CHECKOUT_SECTIONS,
  CHECKOUT_FORMS,
  DORM_INVENTORY_ITEMS,
  SECTION_STATUS,
  RECORD_STATUS,
  DEFAULT_SCHOOL_YEAR,
} from '../utils/checkoutHelpers';

export const ROLES = {
  SUPER_ADMIN: 'superadmin',
  FACULTY: 'faculty',
};

export const ROLE_LABELS = {
  faculty: 'Faculty',
  student: 'Student',
  dean: 'Dean',
  superadmin: 'Super Admin',
};

// Tools with default: true are available to all authenticated faculty.
// Tools with default: false are hidden unless the SuperAdmin explicitly
// grants access to an individual user (stored in user.allowedTools in Firestore).
//
// Tools with capability: true are NOT navigable tiles — they are permission
// grants that unlock behavior inside another tool. getAccessibleTools() never
// returns them, so they never render in the sidebar; the SuperAdmin grant panel
// still lists them via getGrantableTools() (or by iterating TOOLS directly).
export const TOOLS = [
  {
    id: 'relief_roster',
    label: 'Relief Roster',
    icon: '/icons/relief_roster_dark.png',       // dark version — for light backgrounds (Super Admin panel)
    iconDark: '/icons/relief_roster_light.png',  // light version — for dark backgrounds (Sidebar)
    iconType: 'image',
    path: '/tools/relief-roster',
    description: 'Manage substitute coverage and absences',
    default: false,
  },
  {
    id: 'surveys',
    label: 'Surveys',
    icon: '📊',
    path: '/tools/surveys',
    description: 'Create surveys, collect responses, and analyze results',
    default: false,
  },
  {
    id: 'demo',
    label: 'Interactive Demo',
    icon: '🎬',
    path: '/tools/demos',
    description: 'Walk through a guided interactive product demo',
    default: false,
  },
  {
    id: 'transcriber',
    label: 'Transcriber',
    icon: '🎙️',
    path: '/tools/transcriber',
    description: 'Transcribe audio recordings and generate lesson summaries',
    default: false,
  },
  {
    id: 'ap_explorer',
    label: 'AP Score Explorer',
    icon: '📊',
    path: '/tools/ap-explorer',
    description: 'Explore AP score distributions and alignment data',
    default: false,
  },
  {
    id: 'ap_alignment',
    label: 'AP Alignment',
    icon: '📊',
    path: '/tools/ap-alignment',
    description: 'View and analyze AP curriculum alignment data',
    default: false,
  },
  {
    id: 'admissions_explorer',
    label: 'Admissions Explorer',
    icon: '📊',
    path: '/tools/admissions-explorer',
    description: 'Explore admissions data and analytics with PII protection',
    default: false,
  },  
  {
    id: 'nais_explorer',
    label: 'NAIS Explorer',
    icon: '/icons/nais_logo.png',                 // dark version — for light backgrounds (Super Admin panel)       
    iconDark: '/icons/nais_logo_light.png',   // light version — for dark backgrounds (Sidebar)
    path: '/tools/nais-explorer',
    iconType: 'image',
    description: 'Explore NAIS benchmark data and metrics',
    default: false,
  }, 
  {
  id: 'live_script',
  label: 'LiveScript',
  icon: '🎙',
  path: '/tools/live-script',
  description: 'Real-time speech transcription for classroom projection with audio recording and lesson summaries',
  default: false,
  },
  {
  id: 'canvas_analytics',
  label: 'Canvas Analytics',
  icon: '📈',
  path: '/tools/canvas-analytics',
  description: 'Explore Canvas LMS grade and engagement data',
  default: false,
  },
  {
  id: 'survey_monkey',
  label: 'SurveyMonkey',
  icon: '📋',
  path: '/tools/survey-monkey',
  description: 'Access and analyze SurveyMonkey responses',
  default: false,
  },
  {
  id: 'data_analyst',
  label: 'Data Analyst',
  icon: '🧠',
  path: '/tools/data-analyst',
  description: 'AI-powered data analysis and visualization',
  default: false,
  },
  {
  id: 'veracross_upload',
  label: 'Veracross Import',
  icon: '📥',
  path: '/tools/veracross-upload',
  description: 'Import and sync data from Veracross SIS',
  default: false,
  },
  {
    id: "student-action",
    label: "Student After-Action Record",
    description: "Confidential incident documentation for student removals. Teachers submit; administrators review and print.",
    path: "/student-action",
    icon: "📁",
    default: false,
  },
  {
    id: "bell-schedule",
    label: "US Academic Schedule",
    description: "Upper School daily Academic schedule.",
    path: "/tools/bell-schedule",
    icon: "⏰",
    default: true,
  },  
    {
    id: "ms-bell-schedule",
    label: "MS Academic Schedule",
    description: "Middle School daily Academic schedule.",
    path: "/tools/ms-bell-schedule",
    icon: "⏰",
    default: true,
  },
  {
  id: 'bellScheduleAdmin',
  label: 'US Schedule Editor',
  icon: '✏️',
  path: '/bell-editor',
  description: 'Edit bell times, no-school days, and special schedules for the hallway displays',
  default: false,
  },
  {
  id: 'bellScheduleAdminMs',
  label: 'MS Schedule Editor',
  icon: '✏️',
  path: '/ms-bell-editor',
  description: 'Edit bell times, no-school days, and special schedules for the Middle School displays',
  default: false,
  },
  {
    id: 'student_checkout',
    label: 'Student Checkout',
    description: 'End-of-year student exit checklists. Authorized faculty/staff sign off on departmental sections (IT, Library, Athletics, etc.) for departing students.',
    path: '/tools/student-checkout',
    icon: '🎓',
    default: false,
    // NOTE: Visibility is controlled by `allowedTools` like every other tool.
    // The syncCheckoutClaims Cloud Function automatically adds this tool to a
    // user's allowedTools whenever they're assigned to any signoff section in
    // /config/checkoutPointPeople, and removes it when they have no sections
    // left. The SuperAdmin panel toggle is an explicit override (e.g. for
    // admins who need read-only visibility without any signoff power).
  },
  { id: 'checkin', 
    label: 'Student Check-In', 
    description: 'End-of-year exit checklists for grades 7–8 (library, athletics, finance, MS final clearance).',
    path: '/tools/checkin',
    icon: '✅', 
    default: false 
    /* admin-grant only, like checkout */ 
    // optional MS-scoped variant, if you want a separate MS check-in page later:
    // { id: 'ms_checkin', label: 'MS Student Check-In', icon: '✅', default: false },
  },
  {
    id: 'ms_checkout',
    label: 'MS Student Checkout',
    description: 'End-of-year exit checklists for grades 7–8 (library, athletics, finance, MS final clearance).',
    path: '/tools/ms-checkout',
    icon: '🎓',
    default: false,
  },
  {
    id: 'checkout_reports',
    label: 'Checkout Reports',
    description: 'Operational reports on student checkout progress — section bottlenecks, completion buckets, stuck students, almost-done.',
    path: '/tools/checkout-reports',
    icon: '📊',
    default: false,
  },
  {
  id: 'department_review',
  label: 'Department Review',
  description: 'Guided department review sessions — curriculum and AI-use feedback collection for HODs.',
  path: '/tools/department-review',
  icon: '🗂️',
  default: false,
  },
  {
    id: 'stopGoContinue',
    label: 'Stop / Go / Continue',
    path: '/stop-go-continue',
    icon: '🚦',    
    description: 'Faculty PD brainstorm & prioritization tool',
    default: false,                   
    category: 'PD',
  },
  {
    id: 'stopGoContinueAnalyst',
    label: 'SGC Analyst',
    path: '/tools/stop-go-continue-analyst',
    icon: '📊',                       // or '/icons/stoplight.svg' if you'd rather reuse
    description: 'AI synthesis of faculty Stop / Go / Continue PD submissions',
    default: false,                   // grant per-user via SuperAdmin panel
    category: 'PD',
  },
  {
    id: 'phonecheckin',
    label: 'Phone Check-in',
    path: '/tools/phone-checkin',
    icon: '📲',    
    description: 'Student daily phone check-in system',
    default: false,                   
    category: 'Student',
  },
  {
    id: 'cubby_checkin',
    label: 'Cubby Check-In',
    icon: '📱',
    path: '/tools/cubby-checkin',
    description: 'Manually reconcile phone check-in by cubby location',
    default: false,
  },
  {
    id: 'cubby_manager',
    label: 'Cubby Manager',
    icon: '🗄️',
    path: '/tools/cubby-manager',
    description: 'Phone turn-in reports and student cubby assignments (Dean)',
    default: false,
  },
  {
  id: 'phone_emails',
  label: 'Phone Emails (2nd Period)',
  icon: '📧',
  path: '/tools/phone-emails',
  description: 'Email 2nd-period teachers the students who did not turn in phones',
  default: false,
  },
  {
    id: 'lexile_leveler',
    label: 'Reading Level Adapter',
    description: 'Rewrite an article at a target reading level for language learners.',
    path: '/tools/lexile',
    icon: '/icons/lexile.png',
    iconType: 'image',
    default: false,
  },
  {
  id:       "attendance-emails",
  path:     "/attendance-emails",
  label:    "Attendance Emails",
  default:  false,     // Off by default; grant per-user via allowedTools
  icon:     '📧',
  iconType: "image",
  },
  {
  id: 'mops',
  label: 'MOPs Dashboard',
  icon: '⚖️',
  path: '/tools/mops',
  description: 'Track Missed Obligation Points and disciplinary thresholds',
  default: false,
  },
  {
  id: 'unexcused-absences',
  label: 'Absence Dashboard',
  icon: '🚫',
  path: '/tools/absence-dashboard',
  description: 'Track absences with disciplinary thresholds',
  default: false,
  },
  {
    id: 'advisory',
    label: 'Advisory Dashboard',
    icon: '📋',
    path: '/tools/advisory',
    description: 'See your advisees\' absences and tardies',
    default: false,
  },
  // ── Project Manager ──────────────────────────────────────────────────────
  {
    id: 'project_manager',
    label: 'Project Manager',
    icon: '🗓️',
    path: '/tools/project-manager',
    description: 'Time-bound tasks with dependencies, assignments, reminders, comments, and file uploads. You see only the projects you are a member of.',
    default: true,   // participant tier — every faculty member can be invited to a project
  },
  {
    id: 'project_manager_admin',
    label: 'Project Manager — Admin',
    icon: '🛠️',
    description: 'Capability (not a tile): create and own projects, and invite internal + external collaborators. Grant this to project leads via the SuperAdmin panel.',
    default: false,
    capability: true,   // grant-only; never rendered as a navigable tool
  },
  {
    // Everyone-visible tile: opens the Events tool, where faculty see the trips
    // they're chaperoning. The CREATE form inside is gated SEPARATELY by the
    // event_manager capability below, so this default tile does NOT grant
    // creation — it only puts "Events" in every faculty member's sidebar.
    id: "events_home",
    label: "Events",
    icon: "📅",
    description: "Trips you're chaperoning; managers also create and manage events here.",
    path: "/tools/events",
    default: true,
  },
  {
    // The CREATE / MANAGE permission — a capability (no second tile), granted
    // per user in the SuperAdmin panel. Being a chaperone needs NO grant;
    // reserve this for OSL managers who actually create trips. EventManager's
    // `canCreate` checks this toolId, as does the backend `assertManager`.
    id: "event_manager",
    label: "Events — Create / Manage",
    icon: "📅",
    description: "Create and manage events/trips (OSL managers).",
    default: false,
    capability: true,
  },
  {
    id: "event_admin",
    label: "Events — Oversight",
    icon: "🗂️",
    description: "SOC/LT view of all events, rosters, and reports.",
    path: "/tools/events",
    default: false,
    capability: true,   // ← add this: grants access, renders no second tile
  },
  {
    id: 'trip_signup',
    label: 'Trip Sign-Up',
    path: '/tools/trip-signup',
    icon: '🚌',
    default: false,
  },
  {
    id: "trip_spaces",
    label: "Trip Spaces",
    icon: "💬",
    description: "Create Google Chat spaces for boarding trips (QR self-join).",
    path: "/tools/trip-spaces",
    default: false,       // no faculty see it without an explicit SuperAdmin grant
  },
  {
    id: 'coco_manager',
    label: 'Co-Curriculars',
    icon: '🎭',
    path: '/tools/cocurriculars',
    description: 'Create co-curricular activities, assign coaches and teachers, collect student rankings, and run placement.',
    default: false,
  },
  {
    id: 'coco_admin',
    label: 'Co-Curriculars — Oversight',
    icon: '🗂️',
    description: 'Oversight of all co-curricular activities, rosters, placement, and unexcused-absence reports.',
    default: false,
    capability: true,
  },

  /*{
    id: 'gradebook_audit',
    label: 'Gradebook Audit',
    icon: '📋',
    path: '/tools/gradebook-audit',
    description: 'Courses with empty gradebooks (Dean/Admin)',
    default: false,
    dean: true,        // ← deans + superadmins see it by role, no per-user grant
  },*/
];


/**
 * Determines whether a user can access a given tool.
 *
 * @param {string} toolId - The tool's id string (e.g. 'ap_explorer')
 * @param {object} user   - The current user object from AuthContext:
 *                          { isSuperAdmin: bool, allowedTools: string[] }
 * @returns {boolean}
 */

export const canAccessTool = (toolId, user) => {
  if (!user) return false;
  if (user.isSuperAdmin) return true;

  const tool = TOOLS.find((t) => t.id === toolId);
  if (!tool) return false;

  const audience = tool.audience || 'faculty';

  if (user.isStudent) {
    return audience === 'student' || audience === 'all';
  }
  if (audience === 'student') return false;

  // External collaborators (non-@kingsacademy.edu.jo accounts) do NOT inherit
  // default: true tools — they see ONLY what has been explicitly granted to
  // them (the sign-in provisioning grants 'project_manager'). This is what
  // keeps the teacher toolset off an external's dashboard.
  //
  // IMPORTANT: AuthContext must surface one of accountType / isExternal / email
  // on the user object for this to fire. If none is present, an external is
  // treated as internal here and would see default tools — see the wiring note.
  const isExternal =
    user.accountType === 'external' ||
    user.isExternal === true ||
    (typeof user.email === 'string' &&
      !user.email.toLowerCase().endsWith('@kingsacademy.edu.jo'));
  if (isExternal) return (user.allowedTools ?? []).includes(toolId);

  // Dean-scoped tools: visible to deans (and superadmins, already returned true).
  if (tool.dean && user.isDean) return true;

  return tool.default || (user.allowedTools ?? []).includes(toolId);
};

/**
 * Returns the list of tools visible to the given user (navigable tiles only).
 * Capability grants (capability: true) are excluded — they unlock behavior
 * inside other tools and have no page of their own.
 *
 * @param {object} user - { isSuperAdmin: bool, allowedTools: string[] }
 * @returns {Array}
 */
export const getAccessibleTools = (user) => {
  if (!user) return [];
  const navigable = TOOLS.filter((t) => !t.capability);
  if (user.isSuperAdmin) return navigable;
  return navigable.filter((tool) => canAccessTool(tool.id, user));
};

/**
 * Returns every grantable tool/capability for the SuperAdmin permission panel,
 * INCLUDING capability entries (which getAccessibleTools omits). Point the
 * panel's grant list at this if it currently maps over getAccessibleTools;
 * if it already maps over TOOLS directly, no change is needed.
 *
 * @returns {Array}
 */
export const getGrantableTools = () => TOOLS;

/**
 * Whether the user has any Student Checkout permissions — i.e. they're
 * either a superadmin or have at least one assigned signoff section.
 *
 * Used by `canAccessTool` to auto-grant the tool to anyone with a section
 * assignment, and by Sidebar/route guards as the gate for the tool.
 *
 * Mirrors `hasAnyCheckoutPermission` in checkoutHelpers.js (kept here too
 * to avoid forcing roles.js consumers to import from utils).
 *
 * @param {object} user - { isSuperAdmin, checkoutSections }
 * @returns {boolean}
 */
export const hasCheckoutAccess = (user) => {
  if (!user) return false;
  if (user.isSuperAdmin) return true;
  return Array.isArray(user.checkoutSections) && user.checkoutSections.length > 0;
};